import { useState } from 'react';
import { api } from '../api';
import type { Billable, BillingMethod, Document, DocumentKind, DocumentState, TimeEntry } from '../api';
import { Link } from '../router';
import { useSession } from '../session';
import { useData } from '../useData';
import { Shell } from '../components/Shell';
import { TagSelect } from '../components/TagSelect';
import { Empty, hours, hoursOf, Loading, when } from '../components/bits';

/** What we have done and not accounted for, and the documents drawn up from it.
 *
 *  Nothing here issues an invoice and nothing here could: the certified
 *  software does that, through its API, and gives it its number. What is drawn
 *  up here is the draft that invoice is made from, or the statement of hours
 *  for work that was paid for up front — and a draft stays a draft. What
 *  changes is whether anybody still has to act on it. */
export function Billing() {
  const { t, locale } = useSession();
  const [workspaceId, setWorkspaceId] = useState<number | undefined>();
  const [upTo, setUpTo] = useState('');
  const [kind, setKind] = useState<DocumentKind | ''>('');
  const [state, setState] = useState<DocumentState | ''>('open');

  const workspaces = useData(() => api.workspaces(), []);
  const waiting = useData(
    () => api.billable({ workspaceId, upTo: upTo || undefined }),
    [workspaceId, upTo],
  );
  const documents = useData(
    () => api.documents({ workspaceId, kind: kind || undefined, state: state || undefined }),
    [workspaceId, kind, state],
  );

  const reload = () => { waiting.reload(); documents.reload(); };

  // Keyed by client and method: a prepaid package and a monthly retainer are
  // two documents, one of which is not a bill at all.
  const groups = new Map<string, Billable[]>();
  for (const row of waiting.data ?? []) {
    const key = `${row.workspaceId}:${row.billingMethod}`;
    groups.set(key, [...(groups.get(key) ?? []), row]);
  }

  const total = (waiting.data ?? []).reduce((sum, b) => sum + (b.amountCents ?? 0), 0);

  return (
    <Shell>
      <div className="page-head">
        <div className="grow">
          <p className="kicker">{t.toInvoice}</p>
          <h1>{t.billing}</h1>
        </div>
        <p style={{ margin: 0, fontSize: 'var(--fs-heading)' }}>{money(total, locale)}</p>
      </div>

      <div className="row" style={{ marginBottom: 'var(--sp-5)', alignItems: 'flex-end' }}>
        <div style={{ minWidth: 220 }}>
          <label htmlFor="b-client">{t.client}</label>
          <TagSelect
            id="b-client"
            value={workspaceId}
            placeholder={t.all}
            options={(workspaces.data ?? []).map((w) => ({ value: w.id, label: w.name }))}
            onChange={setWorkspaceId}
          />
        </div>
        <div style={{ maxWidth: 170 }}>
          <label htmlFor="b-upto">{t.upTo}</label>
          <input id="b-upto" type="date" value={upTo} onChange={(e) => setUpTo(e.target.value)} />
        </div>
      </div>

      {waiting.loading && !waiting.data ? <Loading /> : null}
      {waiting.data?.length === 0 ? <Empty>{t.nothingToInvoice}</Empty> : null}

      {[...groups.entries()].map(([key, rows]) => (
        <ClientBill key={key} rows={rows} upTo={upTo} onDrafted={reload} />
      ))}

      <h2 style={{ marginTop: 'var(--sp-6)' }}>{t.documents}</h2>

      <div className="row" style={{ margin: 'var(--sp-3) 0' }}>
        <div style={{ maxWidth: 200 }}>
          <label htmlFor="d-kind">{t.documentKind}</label>
          <select id="d-kind" value={kind}
            onChange={(e) => setKind(e.target.value as DocumentKind | '')}>
            <option value="">{t.all}</option>
            <option value="invoice">{t.invoiceDraft}</option>
            <option value="statement">{t.statement}</option>
          </select>
        </div>
        <div style={{ maxWidth: 170 }}>
          <label htmlFor="d-state">{t.status}</label>
          <select id="d-state" value={state}
            onChange={(e) => setState(e.target.value as DocumentState | '')}>
            <option value="open">{t.open}</option>
            <option value="closed">{t.closed}</option>
            <option value="">{t.all}</option>
          </select>
        </div>
      </div>

      {documents.loading && !documents.data ? <Loading /> : null}
      {documents.data?.length === 0 ? <Empty>{t.noDocuments}</Empty> : null}
      <div className="stack" style={{ marginTop: 'var(--sp-3)' }}>
        {(documents.data ?? []).map((d) => (
          <DocumentRow key={d.id} document={d} onChanged={reload} />
        ))}
      </div>
    </Shell>
  );
}

function ClientBill({
  rows, upTo, onDrafted,
}: {
  rows: Billable[];
  upTo: string;
  onDrafted: () => void;
}) {
  const { t, locale } = useSession();
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  // Which entries somebody looked at and left out. They stay loose and turn up
  // in the next document, which is the point: an hour that is not ready to be
  // charged for should not be argued about afterwards.
  const [reviewing, setReviewing] = useState(false);
  const [excluded, setExcluded] = useState<number[]>([]);

  const method = rows[0]!.billingMethod;
  const mine = new Set(rows.map((b) => b.projectId));
  const entries = useData(
    () => (reviewing
      ? api.time({ workspaceId: rows[0]!.workspaceId, to: upTo || undefined, unbilled: true, limit: 500 })
      : Promise.resolve([] as TimeEntry[])),
    [reviewing, rows[0]!.workspaceId, upTo],
  );
  const ours = (entries.data ?? []).filter((e) => mine.has(e.projectId));
  const left = new Set(excluded);

  // The figures on the card follow what is still ticked.
  const dropped = ours.filter((e) => left.has(e.id));
  const droppedMinutesOf = (projectId: number) =>
    dropped.filter((e) => e.projectId === projectId).reduce((sum, e) => sum + e.minutes, 0);
  const minutesOf = (b: Billable) => b.minutes - droppedMinutesOf(b.projectId);
  // The same arithmetic the server does, to the cent: minutes at a rate,
  // rounded half up, in integers. Math.round on top of the +30 rounds twice
  // and 7.50h at 60 euros came out a cent over.
  const amountOf = (b: Billable) =>
    b.hourlyRateCents === null ? null : Math.floor((minutesOf(b) * b.hourlyRateCents + 30) / 60);
  const total = rows.reduce((sum, b) => sum + (amountOf(b) ?? 0), 0);

  // Prepaid work was paid for when the package was bought, so what it needs is
  // an account of the hours, not a bill. A bill needs a price on every line; a
  // statement is still worth sending without one.
  const kind = method === 'prepaid' ? 'statement' : 'invoice';
  const ready = kind === 'statement' || rows.every((b) => b.hourlyRateCents !== null);

  const draft = async () => {
    setBusy(true);
    setProblem(null);
    try {
      await api.draftDocument({
        workspaceId: rows[0]!.workspaceId,
        upTo: upTo || undefined,
        kind,
        method,
        exclude: excluded.length > 0 ? excluded : undefined,
      });
      setExcluded([]);
      setReviewing(false);
      onDrafted();
    } catch (err) {
      setProblem(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="card" style={{ marginBottom: 'var(--sp-4)' }}>
      <div className="between" style={{ marginBottom: 'var(--sp-3)' }}>
        <h3 style={{ margin: 0 }}>
          <Link to={`/clients/${rows[0]!.workspaceId}`}>{rows[0]!.workspaceName}</Link>
          <span className="muted" style={{ fontSize: 13, fontFamily: 'var(--font-display)' }}>
            {' · '}{methodName(t, method, rows[0]!.billingPeriodMonths)}
          </span>
        </h3>
        <span>{money(total, locale)}{kind === 'statement' && (
          <span className="muted" style={{ fontSize: 12 }}> {t.noValue}</span>
        )}</span>
      </div>
      {kind === 'statement' && (
        <p className="muted" style={{ margin: '0 0 var(--sp-3)', fontSize: 13 }}>{t.paidUpFront}</p>
      )}

      <table>
        <tbody>
          {rows.map((b) => (
            <tr key={b.projectId}>
              <td>
                <Link to={`/projects/${b.projectId}`}>{b.projectTitle}</Link>
                <span className="muted" style={{ fontSize: 12 }}>
                  {' · '}{methodName(t, b.billingMethod, b.billingPeriodMonths)}
                  {/* For a package, what gets invoiced is the overrun, so say
                      where the package stands. */}
                  {b.billingMethod === 'prepaid' && b.packageHours !== null &&
                    ` · ${hours(b.usedHours)}/${b.packageHours}h${
                      b.usedHours > b.packageHours ? ` ${t.overrun}` : ''}`}
                </span>
              </td>
              <td className="date muted">
                {b.oldestDay && b.newestDay && b.oldestDay !== b.newestDay
                  ? `${when(b.oldestDay, locale)} – ${when(b.newestDay, locale)}`
                  : b.newestDay ? when(b.newestDay, locale) : ''}
              </td>
              <td className="num">{hoursOf(minutesOf(b))}h</td>
              <td className="num muted">
                {b.hourlyRateCents === null
                  ? <span style={{ color: 'var(--state-waiting)' }}>{t.noRate}</span>
                  : `${money(b.hourlyRateCents, locale)}${t.perHour}`}
              </td>
              <td className="num">{amountOf(b) === null ? '—' : money(amountOf(b)!, locale)}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {reviewing && (
        <table style={{ marginTop: 'var(--sp-3)' }}>
          <tbody>
            {entries.loading && !entries.data ? (
              <tr><td><Loading /></td></tr>
            ) : ours.map((e) => (
              <tr key={e.id} style={left.has(e.id) ? { opacity: 0.45 } : undefined}>
                <td style={{ width: 28 }}>
                  <input
                    type="checkbox"
                    aria-label={e.title ?? ''}
                    checked={!left.has(e.id)}
                    style={{ width: 'auto' }}
                    onChange={() => setExcluded((was) => (
                      was.includes(e.id) ? was.filter((id) => id !== e.id) : [...was, e.id]
                    ))}
                  />
                </td>
                <td className="date muted">{when(e.workedOn, locale)}</td>
                <td>{e.projectTitle}</td>
                <td className="muted">{e.title ?? ''}</td>
                <td className="num">{hoursOf(e.minutes)}h</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {problem && <p className="error" role="alert">{problem}</p>}
      <div className="row" style={{ marginTop: 'var(--sp-3)' }}>
        <button className="primary" onClick={draft} disabled={busy || !ready}>
          {kind === 'statement' ? t.createStatement : t.createDraft} &gt;
        </button>
        <button onClick={() => setReviewing((v) => !v)}>
          {reviewing ? t.hideEntries : t.chooseEntries}
        </button>
        {excluded.length > 0 && (
          <span className="muted" style={{ alignSelf: 'center', fontSize: 13 }}>
            {t.leftOut(excluded.length)}
          </span>
        )}
      </div>
    </section>
  );
}

function DocumentRow({ document: d, onChanged }: { document: Document; onChanged: () => void }) {
  const { t, locale } = useSession();
  const [ref, setRef] = useState('');
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);

  const detail = useData(
    () => (open ? api.document(d.id) : Promise.resolve(d)),
    [open, d.id],
  );

  const act = async (what: () => Promise<unknown>) => {
    setBusy(true);
    try {
      await what();
      onChanged();
    } finally {
      setBusy(false);
    }
  };

  const statement = d.kind === 'statement';
  // The migration left one marker per client for everything invoiced through
  // the old process: no author, no lines, and nothing here to undo about it.
  const elsewhere = d.createdBy === null;

  return (
    <section className="card">
      <div className="between">
        <div>
          <button onClick={() => setOpen((v) => !v)}>
            <strong>{d.workspaceName}</strong>
          </button>
          <span className="muted" style={{ fontSize: 13 }}>
            {' · '}{elsewhere ? t.invoicedElsewhere : statement ? t.statement : t.invoiceDraft}
            {' · '}<span style={{ color: d.state === 'open' ? 'var(--state-new)' : 'var(--state-closed)' }}>
              {d.state === 'open' ? t.open : t.closed}
            </span>
            {d.periodStart && ` · ${when(d.periodStart, locale)}`}
            {d.periodEnd && d.periodEnd !== d.periodStart &&
              ` – ${when(d.periodEnd, locale)}`}
            {d.externalRef && ` · ${d.externalRef}`}
          </span>
        </div>
        <span>{statement ? '' : money(d.totalCents, locale)}</span>
      </div>

      {open && detail.data?.lines && (
        <table style={{ marginTop: 'var(--sp-3)' }}>
          <tbody>
            {detail.data.lines.map((l) => (
              <tr key={l.id}>
                <td>{l.description}</td>
                <td className="num">{hoursOf(l.minutes)}h</td>
                {!statement && (
                  <td className="num muted">{money(l.rateCents, locale)}{t.perHour}</td>
                )}
                {!statement && <td className="num">{money(l.amountCents, locale)}</td>}
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <div className="row" style={{ marginTop: 'var(--sp-3)' }}>
        <Link className="link" to={`/documents/${d.id}`}>{t.openDocument} &gt;</Link>

        {d.state === 'open' && !statement && (
          <div style={{ maxWidth: 200 }}>
            <label htmlFor={`ref-${d.id}`}>{t.invoiceNumber}</label>
            <input id={`ref-${d.id}`} value={ref} placeholder={t.onceItHasOne}
              onChange={(e) => setRef(e.target.value)} />
          </div>
        )}
        {d.state === 'open' && (
          <>
            <button className="primary" disabled={busy}
              onClick={() => act(() => api.closeDocument(d.id, ref || undefined))}>
              {statement ? t.markSent : t.markInvoiced} &gt;
            </button>
            <button disabled={busy} onClick={() => {
              if (window.confirm(t.confirmDiscard)) void act(() => api.discardDocument(d.id));
            }}>
              {t.discard}
            </button>
          </>
        )}
        {d.state === 'closed' && (
          <>
            <span className="muted" style={{ alignSelf: 'center', fontSize: 13 }}>
              {d.closedBy ? `${d.closedBy} · ` : ''}{t.entries(d.entries)}
            </span>
            {!elsewhere && (
              <button disabled={busy} onClick={() => act(() => api.reopenDocument(d.id))}>
                {t.reopen}
              </button>
            )}
          </>
        )}
      </div>
    </section>
  );
}

type Strings = ReturnType<typeof useSession>['t'];

export function methodName(t: Strings, method: BillingMethod, months: number): string {
  if (method === 'prepaid') return t.prepaid;
  // The periodicity is the whole label: "every month" says more than
  // "every period, every month".
  if (method === 'recurring') return t.everyMonths(months);
  return t.occasional;
}

/** Money is kept in cents and only becomes a number with a comma here. */
export function money(cents: number, locale: string): string {
  return new Intl.NumberFormat(locale === 'pt' ? 'pt-PT' : 'en-GB', {
    style: 'currency',
    currency: 'EUR',
  }).format(cents / 100);
}
